import { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Loader2, Plus, RefreshCw, Search, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ErpProjectLinkDialog } from "@/components/ErpProjectLinkDialog";
import { ADMIN_SOLAR_SITES } from "@/constants/testIds";
import {
  activateSolarSite,
  addSolarSiteViewer,
  createMonitoringAccount,
  createSolarSite,
  deactivateSolarSite,
  discoverMonitoringPlants,
  downloadSolarSitesTemplate,
  exportSolarSites,
  importSolarSites,
  listMembers,
  listMonitoringAccounts,
  listSolarSites,
  reimportSolarSiteHistory,
  removeSolarSiteViewer,
  retrySolarSiteOnboarding,
  setupSolarSiteSupport,
  unlinkSolarSiteProject,
  setMonitoringAccountActive,
  testMonitoringAccount,
  updateMonitoringAccount,
  updateSolarSite,
} from "@/lib/api";

const PROVIDERS = [
  ["polycab", "Polycab CloudInverter"],
  ["shinemonitor", "KSolare / ShineMonitor"],
];
const INVERTER_BRANDS = ["polycab", "ksolare", "other"];
const REGISTRY_PAGE_SIZE = 100;
const MAX_REGISTRY_PAGES = 100;
const EMPTY_ACCOUNT = { id: null, provider: "polycab", label: "", username: "", password: "", customer_managed: false };
const EMPTY_SITE = {
  id: null,
  member_id: "",
  monitoring_provider: "polycab",
  monitoring_account_id: "",
  vendor_plant_id: "",
  name: "",
  system_kwp: "",
  tariff_rs_per_kwh: "",
  tz: "Asia/Kolkata",
  inverter_brand: "",
  inverter_model: "",
  datalogger_pn: "",
  vendor_capacity_kw: "",
  lat: "",
  lon: "",
};
const SAFE_API_DETAILS = new Map([
  "Account must pass a recent connection test",
  "Cannot change provider while sites are linked",
  "Monitoring account is not active and recently verified",
  "Monitoring account provider does not match site",
  "Monitoring provider unavailable",
  "Selected vendor plant is unavailable or ambiguous",
  "Site capacity must be configured",
  "Site member account is not active",
  "Site requires one matching monitoring account",
  "Vendor plant is already registered",
].map((detail) => [detail, detail]));
SAFE_API_DETAILS.set(
  "account_customer_managed_conflict",
  "This login is customer-held and its sites belong to different members",
);
SAFE_API_DETAILS.set("onboarding_in_progress", "History import is already running");
SAFE_API_DETAILS.set(
  "owner_mobile_not_verified",
  "The site owner's mobile is not OTP-verified; verify it before setting up support",
);
SAFE_API_DETAILS.set("project_not_linked", "Link the ERP Project first");
SAFE_API_DETAILS.set("viewer_is_owner", "This member owns the site");
SAFE_API_DETAILS.set("viewer_exists", "Already shared with this member");
SAFE_API_DETAILS.set(
  "coordinates_incomplete",
  "Enter both latitude and longitude, or leave both blank",
);
SAFE_API_DETAILS.set(
  "coordinates_out_of_range",
  "Latitude must be between -90 and 90 and longitude between -180 and 180",
);

const safeApiMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? SAFE_API_DETAILS.get(detail) || fallback : fallback;
};

const supportSetupNotice = (setup) => {
  if (!setup) return null;
  const messages = {
    ready: `Support verified: contact ${setup.contact}`,
    created: "Support contact created in Helpdesk",
    updated: `Helpdesk primary contact set to the member (was ${setup.previous_contact || "none"})`,
    no_customer: "The ERP Project has no customer",
    ambiguous: setup.detail || "More than one Helpdesk customer matches; fix in Helpdesk",
    simulated: "Helpdesk writes are off; nothing changed",
    error: setup.detail || "Support setup could not be completed",
  };
  const text = messages[setup.state];
  return text ? { error: setup.state === "error", text } : null;
};

const safeLastError = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("rejected the login")) return "Login rejected, password may have changed";
  if (normalized.includes("timed out")) return "Connection timed out";
  if (normalized.includes("unavailable")) return "Monitoring provider unavailable";
  if (normalized.includes("rejected")) return "Connection rejected by monitoring provider";
  return value ? "Connection check failed" : null;
};

const safeRowError = (value) => {
  const text = String(value || "Invalid row").trim();
  if (!text || text.length > 160 || /https?:|password|secret|token|credential/i.test(text)) return "Invalid row";
  return text.replace(/[\r\n\t]+/g, " ");
};

const Field = ({ label, children }) => (
  <label className="grid min-w-0 gap-1.5 text-sm font-medium text-brand-ink">
    <span>{label}</span>
    {children}
  </label>
);

const NativeSelect = ({ className = "", children, ...props }) => (
  <select
    className={`h-10 w-full rounded-[10px] border border-black/10 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  >
    {children}
  </select>
);

const StatusBadge = ({ active, verified, credentialsState }) => (
  <div className="flex flex-wrap gap-2 text-xs font-semibold">
    <span className={`rounded-full px-2.5 py-1 ${active ? "bg-brand-teal-soft text-brand" : "bg-black/5 text-brand-muted"}`}>
      {active ? "Active" : "Inactive"}
    </span>
    {verified !== undefined && (
      <span className={`rounded-full px-2.5 py-1 ${verified ? "bg-brand-teal-soft text-brand" : "bg-[#fbf1dd] text-brand-gold-deep"}`}>
        {verified ? "Verified" : "Unverified"}
      </span>
    )}
    {credentialsState === "rejected" && (
      <span className="rounded-full bg-red-50 px-2.5 py-1 text-red-800">Login rejected</span>
    )}
  </div>
);

const dateLabel = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unavailable" : date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};
const providerLabel = (value) => PROVIDERS.find(([provider]) => provider === value)?.[1] || value || "Not configured";
const ONBOARDING_PHASES = {
  queued: "History queued",
  daily_importing: "Importing daily totals",
  curve_importing: "Importing daily curves",
  ready: "History ready",
  needs_attention: "Needs attention",
};
const COORDINATE_STATES = {
  pending: "Coordinates pending",
  retrying: "Coordinates retrying",
  resolved: "Coordinates resolved",
  unavailable: "Coordinates unavailable",
};
const dayLabel = (value) => {
  if (!value) return "Not started";
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? "Unavailable"
    : date.toLocaleDateString("en-IN", { dateStyle: "medium", timeZone: "UTC" });
};
const safeOnboardingError = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (!normalized) return "History import needs operator attention";
  if (normalized.includes("timed out")) return "Monitoring provider timed out";
  if (normalized.includes("unavailable")) return "Monitoring provider unavailable";
  if (normalized.includes("eligibility changed")) return "Monitoring account eligibility changed";
  return "History import needs operator attention";
};

const OnboardingProgress = ({ onboarding }) => {
  if (!onboarding) return null;
  const phase = ONBOARDING_PHASES[onboarding.state] || "History status unavailable";
  const coordinates = COORDINATE_STATES[onboarding.coordinate_state] || "Coordinates unknown";
  return (
    <div className="mt-3 rounded-[12px] bg-black/[.035] p-3 text-xs" data-testid="onboarding-progress">
      <div className="flex flex-wrap gap-2 font-semibold text-brand-ink">
        <span>{phase}</span>
        <span>·</span>
        <span>{coordinates}</span>
      </div>
      <div className="mt-2 grid gap-1 text-brand-muted sm:grid-cols-2">
        <span>Daily history: {onboarding.daily_days_written ?? 0} written · {onboarding.daily_days_discovered ?? 0} discovered</span>
        <span>Curves: {onboarding.curve_days_written ?? 0} written · {onboarding.curve_days_discovered ?? 0} discovered</span>
        <span>Daily cursor: {dayLabel(onboarding.daily_cursor)}</span>
        <span>Curve cursor: {dayLabel(onboarding.curve_cursor)}</span>
        {onboarding.scan_floor && <span>Scan start: {dayLabel(onboarding.scan_floor)}</span>}
        {onboarding.curve_drift_days > 0 && <span>Drift days: {onboarding.curve_drift_days}</span>}
        <span>Last update: {dateLabel(onboarding.updated_at)}</span>
        {onboarding.state === "ready" && <span>Completed: {dateLabel(onboarding.completed_at)}</span>}
      </div>
      {onboarding.state === "needs_attention" && (
        <p className="mt-2 font-medium text-amber-800">{safeOnboardingError(onboarding.last_error)}</p>
      )}
    </div>
  );
};

export const collectAllPages = async (
  fetchPage,
  params = {},
  { pageSize = REGISTRY_PAGE_SIZE, maxPages = MAX_REGISTRY_PAGES } = {},
) => {
  const items = [];
  const seenItems = new Set();
  let expectedTotal = null;
  let previousSignature = null;
  for (let page = 1; page <= maxPages; page += 1) {
    const result = await fetchPage({ ...params, page, page_size: pageSize });
    const batch = Array.isArray(result?.items) ? result.items : [];
    const reportedTotal = Number(result?.total);
    if (Number.isFinite(reportedTotal) && reportedTotal >= 0) {
      expectedTotal = expectedTotal === null ? reportedTotal : Math.max(expectedTotal, reportedTotal);
    }
    batch.forEach((item) => {
      const key = item?.id != null ? `id:${item.id}` : `value:${JSON.stringify(item)}`;
      if (!seenItems.has(key)) {
        seenItems.add(key);
        items.push(item);
      }
    });
    if (expectedTotal !== null && items.length >= expectedTotal) {
      return { ...result, total: expectedTotal, page: 1, page_size: items.length, items };
    }
    if (!batch.length) {
      if (expectedTotal !== null && items.length < expectedTotal) {
        throw new Error("Registry pagination ended before the reported total");
      }
      return { ...result, total: items.length, page: 1, page_size: items.length, items };
    }
    const signature = batch.map((item) => item?.id ?? JSON.stringify(item)).join("|");
    if (signature && signature === previousSignature) {
      throw new Error("Registry pagination did not advance");
    }
    previousSignature = signature;
    if (expectedTotal === null && batch.length < pageSize) {
      return { ...result, total: items.length, page: 1, page_size: items.length, items };
    }
  }
  throw new Error("Registry pagination exceeded the safe page limit");
};

const AccountForm = ({ draft, setDraft, disabled, saving, onCancel, onSubmit }) => (
  <form onSubmit={onSubmit} className="rounded-[16px] border border-brand-gold/30 bg-brand-gold-light/40 p-4">
    <h3 className="mb-3 font-semibold">{draft.id ? "Update monitoring account" : "Add monitoring account"}</h3>
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Provider">
        <NativeSelect value={draft.provider} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, provider: event.target.value }))}>
          {PROVIDERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </NativeSelect>
      </Field>
      <Field label="Account label">
        <Input required maxLength={160} value={draft.label} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, label: event.target.value }))} />
      </Field>
      <Field label="Username">
        <Input required maxLength={254} autoComplete="off" value={draft.username} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, username: event.target.value }))} />
      </Field>
      <Field label={draft.id ? "Replacement password (optional)" : "Password"}>
        <Input
          required={!draft.id}
          type="password"
          autoComplete="new-password"
          value={draft.password}
          disabled={disabled}
          onChange={(event) => setDraft((value) => ({ ...value, password: event.target.value }))}
        />
      </Field>
      <label className="flex items-center gap-2 text-sm font-medium text-brand-ink sm:col-span-2">
        <input
          type="checkbox"
          checked={draft.customer_managed}
          disabled={disabled}
          onChange={(event) => setDraft((value) => ({ ...value, customer_managed: event.target.checked }))}
        />
        <span>Customer holds this login</span>
      </label>
    </div>
    <p className="mt-2 text-xs text-brand-muted">Passwords are write-only and are never shown again.</p>
    <div className="mt-4 flex flex-wrap gap-2">
      <Button type="submit" disabled={disabled} className="min-h-10 bg-brand-forest text-white">
        {saving && <Loader2 className="animate-spin" />}{draft.id ? "Update account" : "Save account"}
      </Button>
      <Button type="button" variant="outline" disabled={disabled} onClick={onCancel}>Cancel</Button>
    </div>
  </form>
);

const SiteForm = ({ draft, setDraft, accounts, members, discoveries, disabled, saving, onAccountChange, onCancel, onDiscover, onSubmit }) => {
  const selectAccount = (accountId) => {
    const account = accounts.find((item) => item.id === accountId);
    onAccountChange();
    setDraft((value) => ({
      ...value,
      monitoring_account_id: accountId,
      monitoring_provider: account?.provider || value.monitoring_provider,
      vendor_plant_id: "",
      name: "",
      vendor_capacity_kw: "",
      datalogger_pn: "",
      inverter_brand: "",
      inverter_model: "",
    }));
  };
  const selectDiscovery = (vendorId) => {
    const item = discoveries.find((plant) => plant.vendor_plant_id === vendorId);
    setDraft((value) => item ? {
      ...value,
      vendor_plant_id: item.vendor_plant_id,
      name: item.name || value.name,
      vendor_capacity_kw: item.vendor_capacity_kw || "",
      inverter_brand: item.inverter_brand || "",
      inverter_model: item.inverter_model || "",
      datalogger_pn: item.datalogger_pn || "",
    } : { ...value, vendor_plant_id: vendorId });
  };
  return (
    <form onSubmit={onSubmit} className="premium-card space-y-4" aria-labelledby="site-form-title">
      <div><p className="eyebrow">INACTIVE DRAFT</p><h2 id="site-form-title">{draft.id ? "Update solar site" : "Add solar site"}</h2><p className="text-sm text-brand-muted">Choose ownership and vendor identity explicitly. Saving never activates a site.</p></div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Member">
          <NativeSelect required value={draft.member_id} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, member_id: event.target.value }))}>
            <option value="">Select member</option>
            {members.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.email}</option>)}
          </NativeSelect>
        </Field>
        <Field label="Monitoring account">
          <NativeSelect required value={draft.monitoring_account_id} disabled={disabled} onChange={(event) => selectAccount(event.target.value)}>
            <option value="">Select account</option>
            {accounts.map((item) => <option key={item.id} value={item.id}>{[item.label, item.provider, item.username].filter(Boolean).join(" · ")}</option>)}
          </NativeSelect>
        </Field>
        <div className="grid content-end"><Button type="button" variant="outline" disabled={disabled || !draft.monitoring_account_id} onClick={onDiscover}><RefreshCw />Discover plants</Button></div>
        <Field label="Vendor plant">
          <NativeSelect value={discoveries.some((item) => item.vendor_plant_id === draft.vendor_plant_id) ? draft.vendor_plant_id : ""} disabled={disabled || !discoveries.length} onChange={(event) => selectDiscovery(event.target.value)}>
            <option value="">Choose a discovered plant</option>
            {discoveries.map((item) => <option key={item.vendor_plant_id} value={item.vendor_plant_id}>{item.name || item.vendor_plant_id} · {item.vendor_plant_id}</option>)}
          </NativeSelect>
        </Field>
        <Field label="Vendor plant ID"><Input required maxLength={140} value={draft.vendor_plant_id} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, vendor_plant_id: event.target.value }))} /></Field>
        <Field label="Site name"><Input required maxLength={160} value={draft.name} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, name: event.target.value }))} /></Field>
        <Field label="System capacity (kWp)"><Input required min="0.01" step="0.01" type="number" value={draft.system_kwp} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, system_kwp: event.target.value }))} /></Field>
        <Field label="Tariff (Rs per kWh)"><Input required min="0" step="0.01" type="number" value={draft.tariff_rs_per_kwh} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, tariff_rs_per_kwh: event.target.value }))} /></Field>
        <Field label="Timezone"><Input required maxLength={64} value={draft.tz} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, tz: event.target.value }))} /></Field>
        <Field label="Inverter brand">
          <NativeSelect value={draft.inverter_brand} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, inverter_brand: event.target.value }))}>
            <option value="">Not specified</option>
            {INVERTER_BRANDS.map((brand) => <option key={brand} value={brand}>{brand}</option>)}
          </NativeSelect>
        </Field>
        <Field label="Latitude"><Input data-testid={ADMIN_SOLAR_SITES.latInput} min="-90" max="90" step="any" type="number" value={draft.lat ?? ""} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, lat: event.target.value }))} /></Field>
        <Field label="Longitude"><Input data-testid={ADMIN_SOLAR_SITES.lonInput} min="-180" max="180" step="any" type="number" value={draft.lon ?? ""} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, lon: event.target.value }))} /></Field>
        <p className="text-xs text-brand-muted sm:col-span-2 lg:col-span-3">Needed for the expected-generation curve when the vendor does not send them (Polycab)</p>
        <Field label="Inverter model"><Input maxLength={140} value={draft.inverter_model} disabled={disabled} onChange={(event) => setDraft((value) => ({ ...value, inverter_model: event.target.value }))} /></Field>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={disabled} className="min-h-10 bg-brand-forest text-white">{saving && <Loader2 className="animate-spin" />}Save inactive draft</Button>
        <Button type="button" variant="outline" disabled={disabled} onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
};

export const AdminSolarSites = () => {
  const [registry, setRegistry] = useState({ accounts: [], sites: [], members: [] });
  const [loading, setLoading] = useState(true);
  const [registryLoadSucceeded, setRegistryLoadSucceeded] = useState(false);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState(null);
  const [siteNotices, setSiteNotices] = useState({});
  const [filters, setFilters] = useState({ q: "", provider: "", status: "all" });
  const [appliedSiteParams, setAppliedSiteParams] = useState({});
  const [accountDraft, setAccountDraft] = useState(null);
  const [siteDraft, setSiteDraft] = useState(null);
  const [discoveries, setDiscoveries] = useState([]);
  const [workbook, setWorkbook] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [retryingSites, setRetryingSites] = useState([]);
  const [reimportingSites, setReimportingSites] = useState([]);
  const [projectTarget, setProjectTarget] = useState(null);
  const [viewerSelections, setViewerSelections] = useState({});
  const isBusy = Boolean(busy);
  const hasMonitoringAccounts = registry.accounts.length > 0;
  const setSiteNotice = (siteId, next) =>
    setSiteNotices((value) => ({ ...value, [siteId]: next }));

  useEffect(() => {
    let current = true;
    Promise.all([
      collectAllPages(listMonitoringAccounts),
      collectAllPages(listSolarSites),
      collectAllPages(listMembers),
    ]).then(([accounts, sites, members]) => {
      if (current) {
        setRegistry({ accounts: accounts.items || [], sites: sites.items || [], members: members.items || [] });
        setRegistryLoadSucceeded(true);
      }
    }).catch(() => {
      if (current) setNotice({ error: true, text: "Could not load the solar-site registry" });
    }).finally(() => {
      if (current) setLoading(false);
    });
    return () => { current = false; };
  }, []);

  const replaceAccount = (next) => setRegistry((value) => ({
    ...value,
    accounts: value.accounts.some((item) => item.id === next.id)
      ? value.accounts.map((item) => item.id === next.id ? next : item)
      : [...value.accounts, next],
  }));
  const replaceSite = (next) => setRegistry((value) => ({
    ...value,
    sites: value.sites.some((item) => item.id === next.id)
      ? value.sites.map((item) => {
        if (item.id !== next.id) return item;
        const merged = { ...item, ...next };
        if (next.member === null && next.member_id === item.member_id) merged.member = item.member;
        if (next.monitoring_account === null && next.monitoring_account_id === item.monitoring_account_id) {
          merged.monitoring_account = item.monitoring_account;
        }
        return merged;
      })
      : [...value.sites, next],
  }));

  const applyFilters = async (event) => {
    event.preventDefault();
    setBusy("filters");
    setNotice(null);
    try {
      const params = {
        q: filters.q.trim(),
        provider: filters.provider,
        active: filters.status === "all" ? null : filters.status === "active",
      };
      const result = await collectAllPages(listSolarSites, params);
      setRegistry((value) => ({ ...value, sites: result.items || [] }));
      setAppliedSiteParams(params);
    } catch {
      setNotice({ error: true, text: "Could not filter solar sites" });
    } finally {
      setBusy("");
    }
  };

  const saveAccount = async (event) => {
    event.preventDefault();
    const draft = accountDraft;
    const creating = !draft.id;
    setBusy("account-form");
    setNotice(null);
    try {
      const payload = {
        provider: draft.provider,
        label: draft.label.trim(),
        username: draft.username.trim(),
        customer_managed: draft.customer_managed,
      };
      if (!draft.id || draft.password) payload.password = draft.password;
      const result = draft.id
        ? await updateMonitoringAccount(draft.id, payload)
        : await createMonitoringAccount(payload);
      replaceAccount(result);
      setNotice({ error: false, text: draft.id ? "Account updated." : "Account saved as inactive." });
      if (creating) setAccountDraft(null);
    } catch (error) {
      setNotice({ error: true, text: safeApiMessage(error, "Could not save monitoring account") });
    } finally {
      setAccountDraft((value) => value ? { ...value, password: "" } : value);
      setBusy("");
    }
  };

  const testAccount = async (account) => {
    setBusy(`test-${account.id}`);
    setNotice(null);
    try {
      const result = await testMonitoringAccount(account.id);
      replaceAccount(result);
      setNotice({ error: !result.verified, text: result.verified ? "Connection verified." : safeLastError(result.last_error) });
    } catch (error) {
      setNotice({ error: true, text: safeApiMessage(error, "Connection check failed") });
    } finally {
      setBusy("");
    }
  };

  const toggleAccount = async (account) => {
    setBusy(`account-active-${account.id}`);
    setNotice(null);
    try {
      const result = await setMonitoringAccountActive(account.id, !account.active);
      replaceAccount(result);
      setNotice({ error: false, text: result.active ? "Monitoring account activated." : "Monitoring account deactivated." });
    } catch (error) {
      setNotice({ error: true, text: safeApiMessage(error, "Could not change monitoring-account status") });
    } finally {
      setBusy("");
    }
  };

  const discover = async () => {
    setBusy("discover");
    setNotice(null);
    try {
      const result = await discoverMonitoringPlants(siteDraft.monitoring_account_id);
      setDiscoveries(result.items || []);
      setNotice({ error: false, text: `${result.items?.length || 0} vendor plants ready to choose.` });
    } catch (error) {
      setDiscoveries([]);
      setNotice({ error: true, text: safeApiMessage(error, "Could not discover vendor plants") });
    } finally {
      setBusy("");
    }
  };

  const saveSite = async (event) => {
    event.preventDefault();
    const draft = siteDraft;
    const creating = !draft.id;
    setBusy("site-form");
    setNotice(null);
    try {
      const payload = {
        member_id: draft.member_id,
        monitoring_provider: draft.monitoring_provider,
        monitoring_account_id: draft.monitoring_account_id || null,
        vendor_plant_id: draft.vendor_plant_id.trim(),
        name: draft.name.trim(),
        system_kwp: draft.system_kwp,
        tariff_rs_per_kwh: draft.tariff_rs_per_kwh,
        tz: draft.tz.trim(),
        inverter_brand: draft.inverter_brand || null,
        inverter_model: draft.inverter_model.trim() || null,
        lat: draft.lat === null || draft.lat === undefined || draft.lat === "" ? null : Number(draft.lat),
        lon: draft.lon === null || draft.lon === undefined || draft.lon === "" ? null : Number(draft.lon),
        datalogger_pn: draft.datalogger_pn.trim() || null,
        vendor_capacity_kw: draft.vendor_capacity_kw || null,
      };
      const result = draft.id ? await updateSolarSite(draft.id, payload) : await createSolarSite(payload);
      replaceSite(result);
      setNotice({ error: false, text: "Site saved as an inactive draft." });
      if (creating) {
        setSiteDraft(null);
        setDiscoveries([]);
      }
    } catch (error) {
      setNotice({ error: true, text: safeApiMessage(error, "Could not save solar site") });
    } finally {
      setBusy("");
    }
  };

  const toggleSite = async (site) => {
    setBusy(`site-active-${site.id}`);
    setSiteNotice(site.id, null);
    try {
      const result = site.active ? await deactivateSolarSite(site.id) : await activateSolarSite(site.id);
      replaceSite(result);
      setSiteNotice(site.id, { error: false, text: result.active ? "Solar site activated." : "Solar site deactivated." });
    } catch (error) {
      setSiteNotice(site.id, { error: true, text: safeApiMessage(error, site.active ? "Could not deactivate solar site" : "Could not activate solar site") });
    } finally {
      setBusy("");
    }
  };

  const retryOnboarding = async (site) => {
    setRetryingSites((ids) => [...ids, site.id]);
    setSiteNotice(site.id, null);
    try {
      const result = await retrySolarSiteOnboarding(site.id);
      replaceSite(result);
      setSiteNotice(site.id, { error: false, text: "History import queued again." });
    } catch (error) {
      setSiteNotice(site.id, { error: true, text: safeApiMessage(error, "Could not retry history import") });
    } finally {
      setRetryingSites((ids) => ids.filter((id) => id !== site.id));
    }
  };

  const reimportHistory = async (site) => {
    const confirmed = window.confirm(
      `Re-import the whole history for ${site.name}? The site stays live; daily totals are refreshed from the vendor.`,
    );
    if (!confirmed) return;

    setReimportingSites((ids) => [...ids, site.id]);
    setSiteNotice(site.id, null);
    try {
      const result = await reimportSolarSiteHistory(site.id);
      replaceSite(result);
      setSiteNotice(site.id, {
        error: result.onboarding?.state !== "queued",
        text: result.onboarding?.state === "queued"
          ? "History re-import queued."
          : "Re-import could not be queued. Retry from the site card.",
      });
    } catch (error) {
      setSiteNotice(site.id, { error: true, text: safeApiMessage(error, "Could not re-import history") });
    } finally {
      setReimportingSites((ids) => ids.filter((id) => id !== site.id));
    }
  };

  const unlinkProject = async (site) => {
    setBusy(`site-project-${site.id}`);
    setSiteNotice(site.id, null);
    try {
      replaceSite(await unlinkSolarSiteProject(site.id));
      setSiteNotice(site.id, { error: false, text: "ERP Project unlinked, support is off for this site." });
    } catch (error) {
      setSiteNotice(site.id, { error: true, text: safeApiMessage(error, "Could not unlink the ERP Project") });
    } finally {
      setBusy("");
    }
  };

  const setupSupport = async (site) => {
    setBusy(`site-support-${site.id}`);
    setSiteNotice(site.id, null);
    try {
      const result = await setupSolarSiteSupport(site.id);
      replaceSite(result);
      setSiteNotice(site.id, supportSetupNotice(result.support_setup));
    } catch (error) {
      setSiteNotice(site.id, {
        error: true,
        text: safeApiMessage(error, "Could not set up Helpdesk support"),
      });
    } finally {
      setBusy("");
    }
  };

  const addViewer = async (site) => {
    const memberId = viewerSelections[site.id];
    if (!memberId) return;
    setBusy(`site-viewer-${site.id}`);
    setSiteNotice(site.id, null);
    try {
      replaceSite(await addSolarSiteViewer(site.id, memberId));
      setViewerSelections((value) => ({ ...value, [site.id]: "" }));
      setSiteNotice(site.id, { error: false, text: "Member can now view this solar site." });
    } catch (error) {
      setSiteNotice(site.id, { error: true, text: safeApiMessage(error, "Could not share this solar site") });
    } finally {
      setBusy("");
    }
  };

  const removeViewer = async (site, memberId) => {
    setBusy(`site-viewer-${site.id}`);
    setSiteNotice(site.id, null);
    try {
      replaceSite(await removeSolarSiteViewer(site.id, memberId));
      setSiteNotice(site.id, { error: false, text: "Member no longer has access to this solar site." });
    } catch (error) {
      setSiteNotice(site.id, { error: true, text: safeApiMessage(error, "Could not remove this member") });
    } finally {
      setBusy("");
    }
  };

  const runDownload = async (kind) => {
    setBusy(`download-${kind}`);
    setNotice(null);
    try {
      if (kind === "template") await downloadSolarSitesTemplate();
      else await exportSolarSites();
      setNotice({ error: false, text: kind === "template" ? "Template ready." : "Export ready." });
    } catch {
      setNotice({ error: true, text: kind === "template" ? "Could not download template" : "Could not export solar sites" });
    } finally {
      setBusy("");
    }
  };

  const runImport = async () => {
    if (!workbook) return;
    setBusy("import");
    setNotice(null);
    setImportSummary(null);
    try {
      const result = await importSolarSites(workbook);
      setImportSummary({
        processed: result.processed || 0,
        created: result.created || 0,
        updated: result.updated || 0,
        failed: result.failed || 0,
        errors: (result.errors || []).map((error) => ({ row: error.row, detail: safeRowError(error.detail) })),
      });
      const [accounts, sites] = await Promise.all([
        collectAllPages(listMonitoringAccounts),
        collectAllPages(listSolarSites, appliedSiteParams),
      ]);
      setRegistry((value) => ({
        ...value,
        accounts: accounts.items || [],
        sites: sites.items || [],
      }));
      setNotice({ error: false, text: "Import completed." });
    } catch (error) {
      setNotice({ error: true, text: safeApiMessage(error, "Could not import workbook") });
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="space-y-5" data-testid="admin-solar-sites">
      <div className="page-heading">
        <div><p className="eyebrow">MONITORING REGISTRY</p><h1>Solar Sites</h1><span>Connect vendor accounts and assign each plant to an Alliance member.</span></div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={isBusy || !registryLoadSucceeded} onClick={() => setAccountDraft({ ...EMPTY_ACCOUNT })}><Plus />Add account</Button>
          <Button type="button" disabled={isBusy || !registryLoadSucceeded || !hasMonitoringAccounts} onClick={() => { setSiteDraft({ ...EMPTY_SITE }); setDiscoveries([]); }}><Plus />Add site</Button>
        </div>
      </div>

      {registryLoadSucceeded && !hasMonitoringAccounts && (
        <div role="status" className="rounded-[12px] border border-brand-gold/30 bg-brand-gold-light/50 px-4 py-3 text-sm text-brand-ink">
          <p className="font-semibold">Add a monitoring account before creating a site.</p>
          <p className="mt-1 text-brand-muted">Use Add account to enter the KSolare / ShineMonitor or Polycab login. The account will then appear in the site form.</p>
        </div>
      )}

      {projectTarget && (
        <ErpProjectLinkDialog
          site={projectTarget}
          open={Boolean(projectTarget)}
          onOpenChange={(next) => { if (!next) setProjectTarget(null); }}
          onLinked={(result) => {
            replaceSite(result);
            setProjectTarget(null);
            const setup = supportSetupNotice(result.support_setup);
            if (setup) {
              setSiteNotice(projectTarget.id, { ...setup, text: `ERP Project linked. ${setup.text}` });
            }
          }}
        />
      )}
      {notice && <div role={notice.error ? "alert" : "status"} className={`rounded-[12px] border px-4 py-3 text-sm ${notice.error ? "border-red-200 bg-red-50 text-red-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>{notice.text}</div>}
      {accountDraft && <AccountForm draft={accountDraft} setDraft={setAccountDraft} disabled={isBusy} saving={busy === "account-form"} onCancel={() => setAccountDraft(null)} onSubmit={saveAccount} />}
      {siteDraft && <SiteForm draft={siteDraft} setDraft={setSiteDraft} accounts={registry.accounts} members={registry.members} discoveries={discoveries} disabled={isBusy} saving={busy === "site-form"} onAccountChange={() => setDiscoveries([])} onCancel={() => setSiteDraft(null)} onDiscover={discover} onSubmit={saveSite} />}

      <section className="premium-card space-y-4" aria-labelledby="excel-title">
        <div className="section-head compact"><div><p className="eyebrow">EXCEL REGISTRY</p><h2 id="excel-title">Bulk setup and export</h2><p>Exports contain operational status only-never passwords.</p></div><FileSpreadsheet /></div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" disabled={isBusy} onClick={() => runDownload("template")}><Download />Download template</Button>
          <Button type="button" variant="outline" disabled={isBusy} onClick={() => runDownload("export")}><Download />Export sites</Button>
        </div>
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <Field label="Import .xlsx"><Input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" disabled={isBusy} onChange={(event) => setWorkbook(event.target.files?.[0] || null)} className="w-full" /></Field>
          <Button type="button" disabled={!workbook || isBusy} onClick={runImport}>{busy === "import" ? <Loader2 className="animate-spin" /> : <Upload />}Import workbook</Button>
        </div>
        {importSummary && (
          <div role="status" aria-label="Import summary" className="rounded-[12px] bg-black/[.035] p-4 text-sm">
            <div className="flex flex-wrap gap-x-4 gap-y-1 font-semibold"><span>Processed {importSummary.processed}</span><span>Created {importSummary.created}</span><span>Updated {importSummary.updated}</span><span>Failed {importSummary.failed}</span></div>
            {!!importSummary.errors.length && <ul className="mt-2 list-disc space-y-1 pl-5">{importSummary.errors.map((error) => <li key={`${error.row}-${error.detail}`}>Row {error.row}: {error.detail}</li>)}</ul>}
          </div>
        )}
      </section>

      <form onSubmit={applyFilters} className="premium-card grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_180px_160px_auto] lg:items-end" aria-label="Solar-site filters">
        <Field label="Search sites"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-subtle" size={16} /><Input value={filters.q} disabled={isBusy} onChange={(event) => setFilters((value) => ({ ...value, q: event.target.value }))} className="pl-9" placeholder="Name or vendor plant ID" /></div></Field>
        <Field label="Site provider"><NativeSelect value={filters.provider} disabled={isBusy} onChange={(event) => setFilters((value) => ({ ...value, provider: event.target.value }))}><option value="">All providers</option>{PROVIDERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</NativeSelect></Field>
        <Field label="Site status"><NativeSelect value={filters.status} disabled={isBusy} onChange={(event) => setFilters((value) => ({ ...value, status: event.target.value }))}><option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option></NativeSelect></Field>
        <Button type="submit" disabled={isBusy} className="min-w-0">{busy === "filters" && <Loader2 className="animate-spin" />}Apply filters</Button>
      </form>

      {loading ? (
        <div className="premium-card flex min-h-40 items-center justify-center gap-2" role="status"><Loader2 className="animate-spin" /> Loading solar sites…</div>
      ) : (
        <div className="grid gap-5 xl:grid-cols-2">
          <section className="premium-card space-y-3" aria-labelledby="monitoring-accounts-title">
            <div className="section-head compact"><div><h2 id="monitoring-accounts-title">Monitoring accounts</h2><p>{registry.accounts.length} configured</p></div></div>
            {registry.accounts.map((account) => (
              <article key={account.id} className="rounded-[14px] border border-black/10 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3"><div><h3 className="font-semibold">{account.label}</h3><p className="text-sm text-brand-muted">{account.provider} · {account.username}</p></div><StatusBadge active={account.active} verified={account.verified} credentialsState={account.credentials_state} /></div>
                <div className="mt-3 grid gap-1 text-xs text-brand-muted"><span>{account.password_configured ? "Password configured" : "Password missing"}</span><span>Last tested: {dateLabel(account.last_tested_at)}</span>{safeLastError(account.last_error) && <span>{safeLastError(account.last_error)}</span>}</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" disabled={!!busy} aria-label={`Edit ${account.label}`} onClick={() => setAccountDraft({ id: account.id, provider: account.provider, label: account.label, username: account.username, password: "", customer_managed: account.customer_managed ?? false })}>Edit</Button>
                  <Button type="button" size="sm" variant="outline" disabled={!!busy} aria-label={`Test ${account.label}`} onClick={() => testAccount(account)}>{busy === `test-${account.id}` && <Loader2 className="animate-spin" />}Test connection</Button>
                  <Button type="button" size="sm" disabled={!!busy || (!account.active && !account.verified)} aria-label={`${account.active ? "Deactivate" : "Activate"} ${account.label}`} onClick={() => toggleAccount(account)}>{account.active ? "Deactivate account" : "Activate account"}</Button>
                </div>
              </article>
            ))}
          </section>
          <section className="premium-card space-y-3" aria-labelledby="sites-title">
            <div className="section-head compact"><div><h2 id="sites-title">Registered sites</h2><p>{registry.sites.length} shown</p></div></div>
            {registry.sites.map((site) => {
              const linkedMember = registry.members.find((member) => member.id === site.member_id) || site.member;
              const linkedAccount = registry.accounts.find((account) => account.id === site.monitoring_account_id) || site.monitoring_account;
              const viewers = site.viewers || [];
              const viewerMemberIds = new Set(viewers.map((viewer) => viewer.member_id));
              const availableViewerMembers = registry.members.filter(
                (member) => member.id !== site.member_id && !viewerMemberIds.has(member.id),
              );
              return (
                <article key={site.id} className="rounded-[14px] border border-black/10 bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div><h3 className="font-semibold">{site.name}</h3><p className="text-sm text-brand-muted">{linkedMember?.name || "Unassigned"} · {site.system_kwp} kWp</p></div>
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge active={site.active} />
                      <span
                        data-testid={ADMIN_SOLAR_SITES.projectBadge}
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${site.frappe_project_id ? "bg-brand-teal-soft text-brand" : "bg-black/5 text-brand-muted"}`}
                      >
                        {site.frappe_project_id || "No ERP Project"}
                      </span>
                    </div>
                  </div>
                  <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                    <div><dt className="text-brand-muted">Provider</dt><dd className="font-medium">{providerLabel(site.monitoring_provider)}</dd></div>
                    <div><dt className="text-brand-muted">Linked account</dt><dd className="font-medium">{linkedAccount ? `${linkedAccount.label}${linkedAccount.username ? ` · ${linkedAccount.username}` : ""}` : "No linked monitoring account"}</dd></div>
                    <div><dt className="text-brand-muted">Connection</dt><dd className="font-medium">{linkedAccount ? `Account ${linkedAccount.active ? "active" : "inactive"} · ${linkedAccount.verified ? "Verified" : "Unverified"}` : "Not configured"}</dd></div>
                    <div><dt className="text-brand-muted">Last account test</dt><dd className="font-medium">Last account test: {dateLabel(linkedAccount?.last_tested_at)}</dd></div>
                    <div><dt className="text-brand-muted">Vendor plant</dt><dd className="font-medium">{site.vendor_plant_id}</dd></div>
                    <div><dt className="text-brand-muted">Inverter</dt><dd className="font-medium">{[site.inverter_brand, site.inverter_model].filter(Boolean).join(" · ") || "Not specified"}</dd></div>
                    <div><dt className="text-brand-muted">Coordinates</dt><dd className="font-medium">{Number.isFinite(site.lat) && Number.isFinite(site.lon) ? `${site.lat}, ${site.lon}` : "Coordinates not set"}</dd></div>
                  </dl>
                  <OnboardingProgress onboarding={site.onboarding} />
                  <div className="mt-4 space-y-2">
                    <p className="text-xs font-medium text-brand-muted">Shared with</p>
                    {viewers.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {viewers.map((viewer) => (
                          <span key={viewer.member_id} className="inline-flex items-center gap-1 rounded-full bg-brand-teal-soft px-2.5 py-1 text-xs font-medium text-brand">
                            {viewer.name || viewer.email || viewer.member_id}
                            <button
                              type="button"
                              data-testid={ADMIN_SOLAR_SITES.removeViewer}
                              aria-label={`Remove ${viewer.name || viewer.email || "member"}`}
                              disabled={!!busy}
                              onClick={() => removeViewer(site, viewer.member_id)}
                              className="font-semibold"
                            >
                              Remove
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap items-end gap-2">
                      <Field label="Add member">
                        <NativeSelect
                          value={viewerSelections[site.id] || ""}
                          disabled={!!busy || availableViewerMembers.length === 0}
                          onChange={(event) => setViewerSelections((value) => ({ ...value, [site.id]: event.target.value }))}
                        >
                          <option value="">Choose a member</option>
                          {availableViewerMembers.map((member) => (
                            <option key={member.id} value={member.id}>{member.name || member.email}</option>
                          ))}
                        </NativeSelect>
                      </Field>
                      <Button
                        type="button"
                        size="sm"
                        data-testid={ADMIN_SOLAR_SITES.addViewer}
                        disabled={!!busy || !viewerSelections[site.id]}
                        onClick={() => addViewer(site)}
                      >
                        Add member
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {!site.active && <Button type="button" size="sm" variant="outline" disabled={!!busy} aria-label={`Edit ${site.name}`} onClick={() => { setSiteNotice(site.id, null); setDiscoveries([]); setSiteDraft({ ...EMPTY_SITE, ...site, monitoring_account_id: site.monitoring_account_id || "", inverter_brand: site.inverter_brand || "", inverter_model: site.inverter_model || "", datalogger_pn: site.datalogger_pn || "", vendor_capacity_kw: site.vendor_capacity_kw || "" }); }}>Edit</Button>}
                    <Button type="button" size="sm" disabled={!!busy} aria-label={`${site.active ? "Deactivate" : "Activate"} ${site.name}`} onClick={() => toggleSite(site)}>{site.active ? "Deactivate" : "Activate"}</Button>
                    {/* Support enablement is separate from activation, so the link stays open on an active site. */}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid={`${ADMIN_SOLAR_SITES.projectLinkAction}-${site.id}`}
                      disabled={!!busy || Boolean(site.frappe_project_id)}
                      aria-label={`Link ERP Project for ${site.name}`}
                      onClick={() => { setSiteNotice(site.id, null); setProjectTarget(site); }}
                    >
                      Link ERP Project
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid={`${ADMIN_SOLAR_SITES.projectUnlinkAction}-${site.id}`}
                      disabled={!!busy || !site.frappe_project_id}
                      aria-label={`Unlink ERP Project for ${site.name}`}
                      onClick={() => unlinkProject(site)}
                    >
                      {busy === `site-project-${site.id}` && <Loader2 className="animate-spin" />}
                      Unlink ERP Project
                    </Button>
                    {site.frappe_project_id && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        data-testid={`${ADMIN_SOLAR_SITES.setupSupport}-${site.id}`}
                        disabled={!!busy}
                        aria-label={`Set up support for ${site.name}`}
                        onClick={() => setupSupport(site)}
                      >
                        {busy === `site-support-${site.id}` && <Loader2 className="animate-spin" />}
                        Set up support
                      </Button>
                    )}
                    {site.onboarding?.state === "needs_attention" && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={retryingSites.includes(site.id)}
                        aria-label={`Retry history import for ${site.name}`}
                        onClick={() => retryOnboarding(site)}
                      >
                        {retryingSites.includes(site.id) && <Loader2 className="animate-spin" />}
                        Retry history import
                      </Button>
                    )}
                    {site.active && site.monitoring_account_id && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={
                          reimportingSites.includes(site.id) ||
                          ["queued", "daily_importing", "curve_importing"].includes(site.onboarding?.state)
                        }
                        aria-label={`Re-import history for ${site.name}`}
                        onClick={() => reimportHistory(site)}
                      >
                        {reimportingSites.includes(site.id) && <Loader2 className="animate-spin" />}
                        Re-import history
                      </Button>
                    )}
                  </div>
                  {siteNotices[site.id] && (
                    <div
                      data-testid={`${ADMIN_SOLAR_SITES.siteNotice}-${site.id}`}
                      role={siteNotices[site.id].error ? "alert" : "status"}
                      className={`mt-3 rounded-[12px] border px-4 py-3 text-sm ${siteNotices[site.id].error ? "border-red-200 bg-red-50 text-red-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
                    >{siteNotices[site.id].text}</div>
                  )}
                </article>
              );
            })}
          </section>
        </div>
      )}
    </section>
  );
};

export default AdminSolarSites;
